import {
  Component,
  OnInit,
  signal,
  inject,
  DestroyRef,
  computed,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { catchError, finalize, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User } from '../../shared/UserInterface';
import { AuthService } from '../auth.service';
import { JoinOrganizationComponent } from './components/join-organization.component';
import { ToastService } from '../../../shared/services/toast.service';
import { ErrorHandlingService } from '../../../shared/services/error-handling.service';
import { UserOrganizationService } from '../../../shared/services/user-organization.service';
import { ProfileService } from '../../services/profile.service';
import { to, PATH } from '../../../core/constants/routes';
import {
  ProfileDashboardData,
  ProfileUpdateRequest,
  UserMembership,
  UpcomingActivity,
  UserProfileResponse,
} from '../../models/profile.interface';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { IconType } from '../../../shared/services/icon.service';

// Sub-components
import { ProfileHeaderComponent } from './components/profile-header/profile-header.component';
import { ClubsListComponent } from './components/clubs-list/clubs-list.component';
import { ActivityCardComponent } from './components/activity-card/activity-card.component';
import { UpcomingBookingsComponent } from './components/upcoming-bookings/upcoming-bookings.component';
import { ChangePasswordComponent } from './components/change-password/change-password.component';
import { ChangeEmailComponent } from './components/change-email/change-email.component';
import { GeneralInfoComponent } from './components/general-info/general-info.component';
import { AddressInfoComponent } from './components/address-info/address-info.component';
import { ContactInfoComponent } from './components/contact-info/contact-info.component';
import { SepaAccountInfoComponent } from './components/sepa-account-info/sepa-account-info.component';

/** Minimal callback contract the profile parent needs from any partial-save section. */
interface ProfileSection {
  onSaveComplete(success: boolean): void;
}

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    RouterLink,
    JoinOrganizationComponent,
    IconComponent,
    ProfileHeaderComponent,
    ClubsListComponent,
    ActivityCardComponent,
    UpcomingBookingsComponent,
    ChangePasswordComponent,
    ChangeEmailComponent,
    GeneralInfoComponent,
    AddressInfoComponent,
    ContactInfoComponent,
    SepaAccountInfoComponent,
  ],
  templateUrl: './profile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./profile.component.css'],
})
export class ProfileComponent implements OnInit {
  private toastService = inject(ToastService);
  private errorHandlingService = inject(ErrorHandlingService);
  private userOrganizationService = inject(UserOrganizationService);
  private profileService = inject(ProfileService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);

  @ViewChild(ChangePasswordComponent)
  changePasswordComponent!: ChangePasswordComponent;

  @ViewChild(ChangeEmailComponent)
  changeEmailComponent?: ChangeEmailComponent;

  readonly IconType = IconType;

  /**
   * Rich user state for this view: starts as the JWT-hydrated session user
   * and is enriched in ngOnInit with the full profile from GET /users/profile
   * (which carries fields that aren't in the JWT — address, salutation, etc.).
   */
  currentUser = signal<User | null | undefined>(undefined);
  isRefreshing = signal(false);

  // Dashboard data
  isLoadingDashboard = signal(true);
  upcomingActivities = signal<UpcomingActivity[]>([]);
  memberships = signal<UserMembership[]>([]);
  courseSessionsCount = signal(0);
  subscriptionsCount = signal(0);
  waitlistCount = signal(0);

  // Organizations
  organizations = computed(() => this.userOrganizationService.userOrganizations());

  // OAuth detection (simplified - users with password can change it)
  isOAuthUser = signal(false);

  constructor(public authService: AuthService) {}

  ngOnInit(): void {
    const user = this.authService.currentUserSignal();
    if (user) {
      this.currentUser.set(user);
      this.isOAuthUser.set(!!user.providedBy);
    }

    this.loadFullProfile();
    this.loadDashboardData();
  }

  /**
   * Initial profile hydration. The JWT only carries username + email + roles,
   * so we fetch the rich profile (address, contact, salutation, …) once on
   * mount. Silent failure: the JWT-derived user is good enough as a fallback.
   */
  private loadFullProfile(): void {
    this.profileService
      .getProfile()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((profile) => {
        if (profile) this.applyFullProfile(profile);
      });
  }

  /** Merge a full profile payload into the local user signal. */
  private applyFullProfile(profile: UserProfileResponse): void {
    const current = this.currentUser();
    const username =
      profile.resolvedDisplayName ?? profile.name ?? current?.username ?? '';
    this.currentUser.set({
      ...(current ?? ({} as User)),
      userId: profile.id,
      username,
      email: profile.email,
      accessToken: current?.accessToken ?? '',
      userRoles: current?.userRoles ?? [],
      providedBy: profile.providedBy,
      firstName: profile.firstName,
      lastName: profile.lastName,
      displayName: profile.displayName,
      resolvedDisplayName: profile.resolvedDisplayName,
      salutation: profile.salutation,
      birthdate: profile.birthdate,
      gender: profile.gender,
      addressStreet: profile.addressStreet,
      addressHouseNumber: profile.addressHouseNumber,
      addressPostalCode: profile.addressPostalCode,
      addressCity: profile.addressCity,
      addressCountry: profile.addressCountry,
      contactEmail: profile.contactEmail,
      mobilePhone: profile.mobilePhone,
    });
    this.isOAuthUser.set(!!profile.providedBy);

    // Keep AuthService.currentUserSignal in sync so nav initials/name re-render
    // after profile saves without waiting for a token refresh.
    this.authService.patchCurrentUser({
      username,
      email: profile.email,
      firstName: profile.firstName,
      lastName: profile.lastName,
      displayName: profile.displayName,
      resolvedDisplayName: profile.resolvedDisplayName,
    });
  }

  loadDashboardData(): void {
    this.isLoadingDashboard.set(true);

    this.profileService
      .loadDashboardData()
      .pipe(
        catchError((err) => {
          console.error('Error loading dashboard data:', err);
          return of({
            upcomingBookings: [],
            upcomingActivities: [],
            memberships: [],
            courseSessions: { upcoming: [], past: [], waitlist: [] },
            subscriptionsCount: 0,
          } as ProfileDashboardData);
        }),
        finalize(() => this.isLoadingDashboard.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((data) => {
        this.upcomingActivities.set(data.upcomingActivities);
        this.memberships.set(data.memberships);
        this.courseSessionsCount.set(data.courseSessions.upcoming.length);
        this.waitlistCount.set(data.courseSessions.waitlist.length);
        this.subscriptionsCount.set(data.subscriptionsCount);
      });
  }

  onLogout(): void {
    this.authService.logout();
  }

  onRefreshToken(): void {
    if (this.isRefreshing()) return;
    this.isRefreshing.set(true);

    this.authService
      .refreshToken()
      .pipe(
        catchError(() => {
          this.toastService.error('Token konnte nicht aktualisiert werden');
          return of(null);
        }),
        finalize(() => this.isRefreshing.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        if (result) {
          this.userOrganizationService.refreshUserOrganizations();
          this.toastService.success('Token erfolgreich aktualisiert');
        }
      });
  }

  onChangePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): void {
    this.profileService
      .updateProfile({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      })
      .pipe(
        catchError((err) => {
          this.toastService.error(this.errorHandlingService.getGeneralErrorMessage(err));
          this.changePasswordComponent?.onSaveComplete(false);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        if (result) {
          this.toastService.success('Passwort erfolgreich geändert');
          this.changePasswordComponent?.onSaveComplete(true);
        }
      });
  }

  onChangeEmail(data: { currentPassword: string; email: string }): void {
    this.profileService
      .updateProfile({
        currentPassword: data.currentPassword,
        email: data.email,
      })
      .pipe(
        catchError((err) => {
          this.toastService.error(
            this.errorHandlingService.getGeneralErrorMessage(err),
          );
          this.changeEmailComponent?.onSaveComplete(false);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (result) {
          this.toastService.success('E-Mail erfolgreich geändert');
          this.applyFullProfile(result.user);
          this.changeEmailComponent?.onSaveComplete(true);
        }
      });
  }

  /**
   * Save handler for the general / address / contact sub-sections.
   * Each section emits a partial diff and is notified via `onSaveComplete`.
   * The PATCH response carries the full updated user, so no extra GET is needed.
   */
  onSavePartial(
    section: ProfileSection,
    diff: Partial<ProfileUpdateRequest>,
  ): void {
    if (Object.keys(diff).length === 0) {
      section.onSaveComplete(true);
      return;
    }

    this.profileService
      .updateProfile(diff)
      .pipe(
        catchError((err) => {
          this.toastService.error(
            this.errorHandlingService.getGeneralErrorMessage(err),
          );
          section.onSaveComplete(false);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (result) {
          this.toastService.success('Profil erfolgreich aktualisiert');
          this.applyFullProfile(result.user);
          section.onSaveComplete(true);
        }
      });
  }

  // Navigation methods
  navigateToBookings(): void {
    this.router.navigate(['/my-bookings']);
  }

  navigateToNewBooking(): void {
    this.router.navigate(['/', PATH.book]);
  }

  navigateToCourses(): void {
    this.router.navigate(to.mySessions());
  }

  navigateToSubscriptions(): void {
    this.router.navigate(to.mySubscriptions());
  }
}
